import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
  Typography,
} from '@mui/material';
import { Portfolio } from '../types';
import { PortfolioService } from '../services/portfolioService';

interface CreatePortfolioDialogProps {
  open: boolean;
  onClose: () => void;
  onCreated: (portfolio: Portfolio) => void;
}

const CreatePortfolioDialog: React.FC<CreatePortfolioDialogProps> = ({
  open,
  onClose,
  onCreated,
}) => {
  const [name, setName] = useState('Main Portfolio');
  const [type, setType] = useState<'real' | 'virtual'>('real');
  const [initialFund, setInitialFund] = useState('50000');
  const [maxPositions, setMaxPositions] = useState('4');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleCreate = async () => {
    setError('');
    setLoading(true);

    try {
      const maxPos = parseInt(maxPositions) || 4;
      const portfolio = await PortfolioService.createPortfolio({
        name,
        type,
        initialFundValue: parseFloat(initialFund),
        currentValue: parseFloat(initialFund),
        availableFunds: parseFloat(initialFund),
        totalEarnings: 0,
        isActive: true,
        maxPositions: maxPos,
        positionSize: parseFloat(initialFund) / maxPos,
      });

      onCreated(portfolio);
      onClose();
      
      // Reset form
      setName('Main Portfolio');
      setType('real');
      setInitialFund('50000');
      setMaxPositions('4');
    } catch (err: any) {
      setError(err.message || 'Failed to create portfolio');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Create New Portfolio</DialogTitle>
      <DialogContent>
        <TextField
          autoFocus
          margin="dense"
          label="Portfolio Name"
          fullWidth
          variant="outlined"
          value={name}
          onChange={(e) => setName(e.target.value)}
          sx={{ mb: 2 }}
        />

        <FormControl fullWidth variant="outlined" sx={{ mb: 2 }}>
          <InputLabel>Portfolio Type</InputLabel>
          <Select
            value={type}
            onChange={(e) => setType(e.target.value as 'real' | 'virtual')}
            label="Portfolio Type"
          >
            <MenuItem value="real">Real Portfolio</MenuItem>
            <MenuItem value="virtual">Virtual Portfolio (Testing)</MenuItem>
          </Select>
        </FormControl>

        <TextField
          margin="dense"
          label="Initial Fund Value"
          fullWidth
          variant="outlined"
          type="number"
          value={initialFund}
          onChange={(e) => setInitialFund(e.target.value)}
          InputProps={{
            startAdornment: <InputAdornment position="start">$</InputAdornment>,
          }}
          sx={{ mb: 2 }}
        />

        <TextField
          margin="dense"
          label="Stock Position Capacity"
          fullWidth
          variant="outlined"
          type="number"
          value={maxPositions}
          onChange={(e) => setMaxPositions(e.target.value)}
          helperText={`Each position will be approximately $${(parseFloat(initialFund) / parseInt(maxPositions || '1')).toLocaleString()}`}
          inputProps={{ min: 1, max: 20 }}
        />

        {error && (
          <Typography color="error" variant="body2" sx={{ mt: 2 }}>
            {error}
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={handleCreate}
          variant="contained"
          disabled={loading || !name || !initialFund || parseFloat(initialFund) <= 0}
        >
          Create Portfolio
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreatePortfolioDialog;